import { mutateAction } from "@libs/actions/mutateAction";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@libs/helper/toast";
import { useSession } from "next-auth/react";
import { useFileUpload } from "./useFileUpload";
import { useEffect, useState, useRef } from "react";

interface FolderStructure {
  displayName: string;
  parentId: string | null;
  projectId: string;
  files: {
    displayName: string;
    fileType: string;
    type: string;
    id: string;
    fileSize: string;
  }[];
  subfolders: FolderStructure[];
}

interface UseFolderUploadProps {
  onProgress?: (files: any[]) => void;
  onComplete?: () => void;
  bucketType: string;
  apiEndPoint: string;
  dataRoomProps: any;
}

export const useFolderUpload = ({
  onProgress,
  onComplete,
  bucketType,
  apiEndPoint,
  dataRoomProps,
}: UseFolderUploadProps) => {
  const { uploadFiles, fileStatuses, setFileStatuses, abortFileUpload } =
    useFileUpload({
      bucketType,
      maxSize: 250,
    });
  const { data: session, update } = useSession();

  const [folderFileStatuses, setFolderFileStatuses] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const uploadedFilesRef = useRef<Set<string>>(new Set());
  const abortedFilesRef = useRef<Set<string>>(new Set());
  const progressFilesRef = useRef<any[]>([]);

  // Helper function to extract file name without extension
  const getProcessedFileName = (file: File) => {
    const pattern = /[^a-zA-Z0-9 _.-]/g;
    const fileExtension = file.name.split(".").pop() || "";
    const fileNameWithoutExtension = file.name.substring(
      0,
      file.name.lastIndexOf(".")
    );
    const fileName = fileNameWithoutExtension;
    const fullName = fileName + (fileExtension ? `.${fileExtension}` : "");
    return { fileName, fileExtension, fullName };
  };

  // Sync file statuses from useFileUpload to folder upload
  useEffect(() => {
    if (Object.keys(fileStatuses).length > 0) {
      setFolderFileStatuses((prev: any) => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.entries(fileStatuses).forEach(([fileName, status]) => {
          const prevStatus = updated[fileName];
          const isDifferent =
            !prevStatus ||
            prevStatus.status !== status.status ||
            prevStatus.message !== status.message;

          if (isDifferent) {
            updated[fileName] = { ...status };
            hasChanges = true;

            // Track aborted files
            if (status.status === "aborted") {
              abortedFilesRef.current.add(fileName);
            }
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [fileStatuses]);

  const { mutate: fileUpload } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["file-folder-upload"],
    onSuccess: async (data) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      onComplete?.();
    },
    onError: (error) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  // Individual file abortion for folder uploads
  const abortFolderFile = (fileName: string) => {
    // Use the file upload's abort functionality
    abortFileUpload(fileName);

    // Add to aborted files set
    abortedFilesRef.current.add(fileName);

    // Update the folder file status
    setFolderFileStatuses((prev: any) => ({
      ...prev,
      [fileName]: { status: "aborted", message: "File upload cancelled" },
    }));

    // Update progress to reflect aborted status
    const updatedProgress = progressFilesRef.current.map((file) => ({
      ...file,
      status: file.fileName === fileName ? "aborted" : file.status,
    }));
    progressFilesRef.current = updatedProgress;
    onProgress?.(updatedProgress);
  };

  const handleFolderUpload = async (
    files: FileList,
    projectId: string,
    parentId = null,
    pathPrefix?: string
  ) => {
    const selectedFiles = Array.from(files);
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    uploadedFilesRef.current.clear();
    abortedFilesRef.current.clear(); // Initialize progress tracking
    const initialProgress = selectedFiles.map((file) => {
      const { fileName, fileExtension, fullName } = getProcessedFileName(file);
      return {
        fileName: fullName,
        fileWithPath: file.webkitRelativePath,
        progress: 0,
        status: "uploading",
        fileType: fileExtension.toLowerCase() || "unknown",
      };
    });

    progressFilesRef.current = initialProgress;

    // Initialize folder file statuses
    const initialStatuses: any = {};
    initialProgress.forEach((file) => {
      initialStatuses[file.fileName] = { status: "pending" };
    });
    setFolderFileStatuses((prev) => ({ ...prev, ...initialStatuses }));

    onProgress?.(initialProgress);

    if (!selectedFiles[0]) {
      setIsUploading(false);
      return;
    }

    const rootFolderName = selectedFiles[0].webkitRelativePath.split("/")[0];
    const folderStructure: FolderStructure = {
      displayName: rootFolderName!,
      parentId: parentId,
      projectId,
      files: [],
      subfolders: [],
    };

    try {
      // Create a map to store all folders
      const folderMap = new Map<string, FolderStructure>();
      folderMap.set(rootFolderName!, folderStructure);

      // First pass: Create folder structure
      for (const file of selectedFiles) {
        const pathParts = file.webkitRelativePath.split("/");
        let currentPath = pathParts[0];

        for (let i = 1; i < pathParts.length - 1; i++) {
          const folderName = pathParts[i];
          const parentPath = currentPath;
          currentPath = `${currentPath}/${folderName}`;

          if (!folderMap.has(currentPath)) {
            const newFolder: FolderStructure = {
              displayName: folderName!,
              parentId: parentPath!,
              projectId,
              files: [],
              subfolders: [],
            };
            folderMap.set(currentPath, newFolder);

            const parentFolder = folderMap.get(parentPath!);
            if (parentFolder) {
              parentFolder.subfolders.push(newFolder);
            }
          }
        }
      }

      // Second pass: Upload files by directory
      const filesByDirectory = new Map<string, File[]>();
      for (const file of selectedFiles) {
        const pathParts = file.webkitRelativePath.split("/");
        const directoryPath = pathParts.slice(0, -1).join("/");

        if (!filesByDirectory.has(directoryPath)) {
          filesByDirectory.set(directoryPath, []);
        }
        filesByDirectory.get(directoryPath)?.push(file);
      }

      // Process each directory's files
      for (const [dirPath, dirFiles] of filesByDirectory.entries()) {
        // Filter out aborted files before processing
        const nonAbortedFiles = dirFiles.filter((file) => {
          const { fileName, fullName } = getProcessedFileName(file);
          return !abortedFilesRef.current.has(fullName);
        });

        if (nonAbortedFiles.length === 0) continue;

        const dataTransfer = new DataTransfer();
        nonAbortedFiles.forEach((file) => dataTransfer.items.add(file));

        if (!nonAbortedFiles[0]) continue;

        // Create the path string with optional prefix
        const relativePath = nonAbortedFiles[0].webkitRelativePath
          .split("/")
          .slice(0, -1)
          .join("/");

        const uploadPath = pathPrefix
          ? `${pathPrefix}/${relativePath}`
          : relativePath;

        try {
          const uploadedFiles = await uploadFiles(
            dataTransfer.files,
            uploadPath
          );

          if (uploadedFiles && uploadedFiles.length > 0) {
            const folderForFiles = folderMap.get(dirPath);
            if (folderForFiles) {
              // Only add successfully uploaded files to the folder structure
              const successfulFiles = uploadedFiles
                .filter((file: any) => {
                  const { fileName, fullName } = getProcessedFileName({
                    name: file.originalFileName,
                  } as File);
                  // Check if file was not aborted
                  return !abortedFilesRef.current.has(fullName);
                })
                .map((file: any) => {
                  const { fileName, fileExtension, fullName } =
                    getProcessedFileName({
                      name: file.originalFileName,
                    } as File);
                  uploadedFilesRef.current.add(fullName);
                  return {
                    displayName: file.originalFileName,
                    fileType: file.type || "",
                    type: file.type || "",
                    id: file.s3ObjectKey,
                    fileSize: file.fileSize.toString(),
                  };
                });

              folderForFiles.files = successfulFiles;
            } // Update progress for successful uploads
            const successfulFileNames = uploadedFiles
              .filter((file: any) => {
                const { fileName, fullName } = getProcessedFileName({
                  name: file.originalFileName,
                } as File);
                return !abortedFilesRef.current.has(fullName);
              })
              .map((file: any) => {
                const { fileName, fullName } = getProcessedFileName({
                  name: file.originalFileName,
                } as File);
                return fullName;
              });

            // Update progress
            const updatedProgress = progressFilesRef.current.map((file) => ({
              ...file,
              progress: successfulFileNames.includes(file.fileName)
                ? 100
                : file.progress,
              status: abortedFilesRef.current.has(file.fileName)
                ? "aborted"
                : successfulFileNames.includes(file.fileName)
                  ? "complete"
                  : file.status,
            }));

            progressFilesRef.current = updatedProgress;
            onProgress?.(updatedProgress);
          }

          // Add a small delay to ensure status updates are captured
          await new Promise((resolve) => setTimeout(resolve, 50));
        } catch (uploadError: any) {
          console.error(
            `Error uploading files in directory ${dirPath}:`,
            uploadError
          );

          // Update status for failed files in this directory (excluding aborted ones)
          nonAbortedFiles.forEach((file) => {
            const pattern = /[^a-zA-Z0-9 _.-]/g;
            const fileName = file.name;

            if (!abortedFilesRef.current.has(fileName)) {
              setFolderFileStatuses((prev: any) => ({
                ...prev,
                [fileName]: {
                  status: "error",
                  message: uploadError.message || "Upload failed",
                },
              }));
            }
          });
        }
      }

      // Only upload folder structure if we have successfully uploaded files (excluding aborted ones)
      if (uploadedFilesRef.current.size > 0) {
        // Filter out folders that have no successfully uploaded files
        const cleanFolderStructure = filterEmptyFolders(folderStructure);

        if (
          cleanFolderStructure &&
          (cleanFolderStructure.files.length > 0 ||
            cleanFolderStructure.subfolders.length > 0)
        ) {
          fileUpload({
            endPoint: apiEndPoint,
            type: "post",
            data: { ...cleanFolderStructure, ...(dataRoomProps || {}) },
            session,
            update,
          });
        }
      }

      // Final status sync
      setTimeout(() => {
        setFolderFileStatuses((prev: any) => ({
          ...prev,
          ...fileStatuses,
        }));
      }, 200);

      // Update final progress for all files
      const finalProgress = progressFilesRef.current.map((file) => ({
        ...file,
        progress: abortedFilesRef.current.has(file.fileName)
          ? 0
          : uploadedFilesRef.current.has(file.fileName)
            ? 100
            : 0,
        status: abortedFilesRef.current.has(file.fileName)
          ? "aborted"
          : uploadedFilesRef.current.has(file.fileName)
            ? "complete"
            : "failed",
      }));

      progressFilesRef.current = finalProgress;
      onProgress?.(finalProgress);
    } catch (error: any) {
      console.error("Error uploading folder structure:", error);
      toast("Failed to upload folder structure", { type: "error" });

      // Update final progress considering aborted files
      const finalProgress = progressFilesRef.current.map((file) => ({
        ...file,
        progress: abortedFilesRef.current.has(file.fileName)
          ? 0
          : uploadedFilesRef.current.has(file.fileName)
            ? 100
            : 0,
        status: abortedFilesRef.current.has(file.fileName)
          ? "aborted"
          : uploadedFilesRef.current.has(file.fileName)
            ? "complete"
            : "failed",
      }));

      progressFilesRef.current = finalProgress;
      onProgress?.(finalProgress);
    } finally {
      setIsUploading(false);
    }
  };

  // Helper function to filter out folders with no successfully uploaded files
  const filterEmptyFolders = (
    folder: FolderStructure
  ): FolderStructure | null => {
    const filteredSubfolders = folder.subfolders
      .map((subfolder) => filterEmptyFolders(subfolder))
      .filter((subfolder) => subfolder !== null) as FolderStructure[]; // Only include folders that have non-aborted files
    const nonAbortedFiles = folder.files.filter((file) => {
      const { fileName, fullName } = getProcessedFileName({
        name: file.displayName,
      } as File);
      return !abortedFilesRef.current.has(fullName);
    });

    if (nonAbortedFiles.length === 0 && filteredSubfolders.length === 0) {
      return null;
    }

    return {
      ...folder,
      files: nonAbortedFiles,
      subfolders: filteredSubfolders,
    };
  };

  return {
    handleFolderUpload,
    folderFileStatuses,
    setFileStatuses: setFolderFileStatuses,
    abortFolderFile,
    isUploading,
  };
};

export const acceptedFiles = [
  // Videos
  "video/x-msvideo",
  "video/quicktime",
  "video/webm",
  "video/x-ms-wmv",
  "video/mpeg",
  "video/3gpp",
  "video/x-matroska",
  "video/mp4",
  "video/x-flv",

  // Location
  "application/vnd.google-earth.kml+xml",
  "application/vnd.google-earth.kmz",
  "application/x-dbf",
  "application/octet-stream", // .shp, .shx

  // Gallery
  "image/tiff",
  "image/jpeg",
  "image/webp",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/svg+xml",
  "image/heic",

  // Documents
  "application/pdf",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/markdown",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.openxmlformats-officedocument.presentationml.template",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/rtf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/html",
  "application/vnd.oasis.opendocument.presentation",
  "text/plain",
  "application/xml",
  "text/xml",
  "application/json",
];
